import { supabase } from '@lib/supabase/client'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { memCache } from '@lib/cache'

export interface AuthResponse {
  user: User | null
  session: Session | null
  error: AuthError | null
}

export function clearStoredAuthState() {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('auth_refresh_token')
  localStorage.removeItem('user_role')
  localStorage.removeItem('last_activity')
  localStorage.removeItem('auth_expires_at')
}

export type AuthRole = 'patient' | 'provider'

export function getRoleFromUser(user: User | null): AuthRole | null {
  if (!user) return null

  const metadataRole = user.user_metadata?.role ?? user.user_metadata?.user_type ?? user.app_metadata?.role
  if (metadataRole === 'patient' || metadataRole === 'provider') {
    return metadataRole
  }

  return null
}

export async function resolveRoleForUser(user: User | null): Promise<AuthRole | null> {
  const metadataRole = getRoleFromUser(user)
  if (metadataRole) return metadataRole
  if (!user) return null

  const [{ data: patient }, { data: provider }] = await Promise.all([
    supabase.from('patients').select('id').eq('auth_id', user.id).maybeSingle(),
    supabase.from('providers').select('id').eq('auth_id', user.id).maybeSingle(),
  ])

  if (patient?.id) return 'patient'
  if (provider?.id) return 'provider'
  return null
}

// Patient Sign Up
export async function signUpPatient(
  email: string,
  password: string,
  userData: {
    firstName: string
    lastName: string
    phone: string
    dateOfBirth: string
  }
): Promise<AuthResponse> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        role: 'patient',
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        dateOfBirth: userData.dateOfBirth,
      },
    },
  })

  return { user: data.user, session: data.session, error }
}

// Patient Login
export async function loginPatient(
  email: string,
  password: string
): Promise<AuthResponse> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })

  return { user: data.user, session: data.session, error }
}

// Provider Sign Up
export async function signUpProvider(
  email: string,
  password: string,
  userData: {
    firstName: string
    lastName: string
    phone: string
    specialty: string
    licenseNumber: string
  }
): Promise<AuthResponse> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        role: 'provider',
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        specialty: userData.specialty,
        licenseNumber: userData.licenseNumber,
      },
    },
  })

  return { user: data.user, session: data.session, error }
}

// Provider Login
export async function loginProvider(
  email: string,
  password: string
): Promise<AuthResponse> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })

  return { user: data.user, session: data.session, error }
}

// Verify Email OTP
export async function verifyEmailOTP(
  email: string,
  token: string
): Promise<AuthResponse> {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'email',
  })

  return { user: data.user, session: data.session, error }
}

// Logout
export async function logout(): Promise<{ error: AuthError | null }> {
  clearStoredAuthState()
  memCache.clear()

  const { error } = await supabase.auth.signOut({ scope: 'local' })
  return { error }
}

// Get Current Session
export async function getCurrentSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession()
  return data.session
}

// Get Current User
export async function getCurrentUser(): Promise<User | null> {
  const { data } = await supabase.auth.getUser()
  return data.user
}

// Password Reset
export async function resetPassword(email: string): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.resetPasswordForEmail(email)
  return { error }
}

// Update Password
export async function updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  })
  return { error }
}
